import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { constants, ILabelValueConstant } from 'app/constants/constants';
import { GlobalService } from 'app/global/global.service';
import { HttpActionState } from 'app/main/pages/users/userslisting/store/users-listing.reducers';
import { BehaviorSubject, catchError, combineLatest, debounceTime, filter, finalize, map, Observable, of, Subject, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs';
import { IRevenueDTO } from '../../../contract-control.dto';
import { ContractControlPermissionService } from '../../../contract-control.permission.service';
import { ContractControlService } from '../../../contract-control.service';
import { ICostRevenueConversionRates, ITotalCost } from '../../cc-cost/cost.dto';
import { CONTRACT_DETAIL_FACADE_TOKEN, IContractDetailFacade } from '../../store/contract-detail-facade.token';
import { CCRevenueHelperService } from '../cc-revenue-helper.services';
import { FlatpickrDirective } from 'angularx-flatpickr';

import { ActionEnum } from '../../../modals/add-cost-modal/add-cost-modal.component';

@Component({
  selector: 'app-add-revenue-sidebar',
  templateUrl: './add-revenue-sidebar.component.html',
  styleUrls: ['./add-revenue-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class AddRevenueSidebarComponent implements OnInit, OnDestroy {
  
  @ViewChild('billingEndPicker', { static: false }) billingEndPicker: FlatpickrDirective;
  @Input() revenue: IRevenueDTO;
  @Input() actionFrom: ActionEnum = ActionEnum.EDITING_CONTRACT;
  @Input() document_type_id: number;
  @Input() contract_control_id: number;
  
  public revenueConversationRates: ICostRevenueConversionRates;
  public submitted: boolean = false;
  public loading: boolean = false;
  private isLoadingConversionRates: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private conversionSuccessful: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  public contract$ = this._contractFacade.selectedContract$;
  public selectedRevenue$ = this._contractFacade.selectedRevenue$;
  public canEditContractCard$ = of(false);
  public hasFullAccess$ = of(false);
  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private contractControlService: ContractControlService,
    private globalService: GlobalService,
    private _translocoService: TranslocoService,
    private _coreSidebarService: CoreSidebarService,
    private _contractControlPermissionService: ContractControlPermissionService,
    private _ccRevenueHelperService: CCRevenueHelperService,
    @Inject(CONTRACT_DETAIL_FACADE_TOKEN) public _contractFacade: IContractDetailFacade
  ) { }

  public chooseCurrency: ILabelValueConstant[] = constants.contract_control_currencies;
  public systemCurrency: string;
  public revenueForm: FormGroup;
  public totalCost: ITotalCost;
  private actionQueue: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public chooseDaysMonth: Array<ILabelValueConstant> = [
    { label: 'Month(s)', value: 'm' }
  ];

  public billingFrequencyOptions: Array<ILabelValueConstant> = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Quarterly', value: 'quarterly' },
    { label: 'Semi-Annually', value: 'semi_annually' },
    { label: 'Annually', value: 'annually' },
    { label: 'Every two years', value: 'two_years' },
    { label: 'Every three years', value: 'three_years' },
    { label: 'Every five years', value: 'five_years' },
    { label: 'One-time', value: 'one_time' }
  ];



  ngOnInit(): void {
    this.systemCurrency = this.globalService.getSystemCurrency();

    this.initRevenueForm(this.revenue);

    if (this.actionFrom === ActionEnum.CREATING_CONTRACT) {
        if (this.revenue) {
            this.revenueForm.patchValue(this.revenue);
        }
    } else {
        combineLatest([
            this._contractFacade.selectedContract$.pipe(filter(Boolean)),
            this.selectedRevenue$
          ])
          .pipe(takeUntil(this.destroy$))
          .subscribe(([contract, revenue]) => {
            this.canEditContractCard$ = this._contractControlPermissionService.canEditContract(
              contract.document_type_id,
              contract.document_type_id,
              contract.category_ids,
              contract
            );
            this.hasFullAccess$ = this._contractControlPermissionService.hasFullAccess(
              contract.document_type_id,
              contract.document_type_id,
              contract.category_ids,
              contract
            );
      
            this.resetFormProps();
            if (revenue) {
              this.revenueForm.patchValue(revenue);
            }
            this.revenueForm.patchValue({
              document_type_id: contract.document_type_id,
              contract_control_id: contract.id
            });
      
            this.conversionRates();
            this.disableFormControls();
          });
    }
  }





  /**
   * Initializes the reactive form for cost data entry and editing.
   *
   * @param data - A partial object containing initial values for the cost form fields.
   *               If a field is not provided, a default value is used.
   *
   * The form includes the following controls:
   * - `id`: The unique identifier of the cost.
   * - `document_type_id`: The associated document type ID.
   * - `contract_control_id`: The associated contract control ID.
   * - `description`: A description of the cost.
   * - `revenue_amount`: The monetary amount of the cost.
   * - `billing_frequency`: The frequency at which billing occurs.
   * - `billing_start`: The start date or marker for billing.
   * - `cost_currency`: The currency of the cost.
   * - `billing_end`: The end date or marker for billing.
   * - `has_billing_end_date`: Indicates if there is a billing end date.
   *
   * The form is set to update on blur events. On any value change, the `updateCost` method is triggered.
   * 
   * @remarks
   * - The method sets up the form with default values if data is missing.
   * - Value changes are subscribed to for triggering cost updates.
   * - There are commented sections for future improvements, such as debouncing and permission-based control enabling/disabling.
   */
  initRevenueForm(data: Partial<IRevenueDTO | null>): void {
    this.revenueForm = this.formBuilder.group({
      id: [data?.id],
      document_type_id: [this.document_type_id, Validators.required],
      contract_control_id: [this.contract_control_id, Validators.required],
      description: [data?.description, Validators.required],
      revenue_amount: [data?.revenue_amount, [Validators.required, Validators.min(0)]],
      billing_frequency: [data?.billing_frequency, [Validators.required]],
      billing_start: [data?.billing_start, [Validators.required]],
      revenue_currency: [data?.revenue_currency ?? this.systemCurrency],
      billing_end: [data?.billing_end],
      has_billing_end_date: [data?.has_billing_end_date ?? false],
      revenue_converted_amount: [data?.revenue_converted_amount],
      conversion_rate: [data?.conversion_rate],
      conversion_date: [data?.conversion_date]
    }, { validators: [this.startBeforeEndValidator] });


    this.revenueForm.get('revenue_amount')?.valueChanges.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.conversionRates()
    });


    
    this.revenueForm.get('has_billing_end_date')?.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
    ).subscribe((hasBillingEndDate) => {
      this.disabledBillingEnd(!hasBillingEndDate);
    });

    this.revenueForm.get('revenue_currency').valueChanges.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.conversionRates();
    });

    this.removeValidatorsIfCreatingContract();
  }

  private removeValidatorsIfCreatingContract(): void {
    if(this.actionFrom === ActionEnum.CREATING_CONTRACT){
      this.revenueForm.get('contract_control_id')?.removeValidators([Validators.required]);
      this.revenueForm.get('contract_control_id')?.updateValueAndValidity();

      this.revenueForm.get('document_type_id')?.removeValidators([Validators.required]);
      this.revenueForm.get('document_type_id')?.updateValueAndValidity();
    }
  }







  /**
   * Disables or enables all controls in the `revenueForm` form group based on the user's permission.
   * 
   * Subscribes to the `hasPermission$` observable and, upon receiving the first value,
   * iterates through each control in the form. If the user does not have permission,
   * all controls are disabled; otherwise, they are enabled.
   *
   * @private
   */
  private disableFormControls(): void {
    this.canEditContractCard$.pipe(
      take(1)
    ).subscribe(hasPermission => {
      Object.keys(this.revenueForm.controls).forEach(controlName => {
        const control = this.revenueForm.get(controlName);
        hasPermission === false ? control.disable() : control.enable();
        control.updateValueAndValidity();
      });
    });
  }



    /**
   * Enables or disables the 'billing_end' form control in the costForm.
   * 
   * This method resets the value of the 'billing_end' control, then enables or disables it
   * based on the provided parameter. After changing the state, it updates the control's
   * value and validity.
   *
   * @param shouldDisabled - If true, disables the 'billing_end' control; otherwise, enables it. Defaults to false.
   */
  private disabledBillingEnd(shouldDisabled: boolean = false): void {
    const billingEndControl = this.revenueForm.get('billing_end');

    if (shouldDisabled) {
      this.billingEndPicker?.instance?.clear();
      billingEndControl?.setValue(null, { emitEvent: false });
      billingEndControl?.disable({ emitEvent: false });
    } else {
      billingEndControl?.enable({ emitEvent: false });
    }

    billingEndControl?.updateValueAndValidity({ emitEvent: false });
  }




  /**
   * Custom validator to ensure that the start date is before the end date in a form group.
   *
   * This validator checks the values of 'billing_start' and 'billing_end' controls within the provided form group.
   * If both dates are present and the start date is after the end date, it returns a validation error object.
   * If the dates are valid or not both present, it returns null, indicating no validation errors.
   * @param formGroup - The FormGroup containing 'billing_start' and 'billing_end' controls to be validated.
   * @returns A validation error object if the start date is after the end date, otherwise null.
   */
  private startBeforeEndValidator(formGroup: FormGroup) {
    const start = formGroup.get('billing_start')?.value;
    const end = formGroup.get('billing_end')?.value;
    if (start && end && new Date(start) > new Date(end)) {
      return { startAfterEnd: true };
    }
    return null;
  }




  get form() { return this.revenueForm.controls; }





  /**
   * Observable that emits a boolean indicating whether a cost deletion request is currently in progress.
   * 
   * @returns {Observable<boolean>} Emits `true` if the delete cost request state is `Busy`, otherwise `false`.
   */
  get isDeleting$(): Observable<boolean> {
    return this._contractFacade.deleteRevenueRequestState$.pipe(
      take(1),
      map(state => state === HttpActionState.Busy)
    );
  }




  
  /** 
   * Handles changes to the billing start date input field.
   * This method updates the `billing_start` form control in the `costForm` FormGroup
   * based on the value from the input event. If the input value is empty,
   * it sets the control's value to `null`; otherwise, it sets it to the input value.
   *
   * @param event - The input event from the billing start date field.
   */
  onBillingStartDateChange(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value;
    this.revenueForm.get('billing_start')?.setValue(value || null, {emitEvent: false});
  }




  /** 
   * Handles changes to the billing end date input field.
   *
   * This method updates the `billing_end` form control in the `revenueForm` FormGroup
   * based on the value from the input event. If the input value is empty,
   * it sets the control's value to `null`; otherwise, it sets it to the input value.
   *
   * @param event - The input event from the billing end date field.
   */
  onBillingEndDateChange(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value;
    this.revenueForm.get('billing_end')?.setValue(value || null, {emitEvent: false});
  }





  /**
   * Initiates the deletion of the currently selected revenue item.
   * 
   * This method checks if a delete request is already in progress. If not, it triggers the deletion
   * of the selected revenue item via the contract control facade. After the deletion request completes
   * (either successfully or with failure), it toggles the sidebar for adding revenue.
   *
   * @remarks
   * - Prevents duplicate deletion requests by checking the current request state.
   * - Subscribes to the deletion request state to handle post-deletion UI updates.
   */
  deleteRevenue(): void {
    this._contractFacade.deleteRevenueRequestState$.pipe(
      take(1),
      withLatestFrom(this._contractFacade.selectedRevenue$),
    ).subscribe(([state, selectedRevenue]) => {
      if (state === HttpActionState.Busy) {
        return;
      }

      this._contractFacade.deleteRevenue(selectedRevenue.id);

      this._contractFacade.deleteRevenueRequestState$.pipe(
        takeUntil(this.destroy$),
        filter(s => s === HttpActionState.Done || s === HttpActionState.Failed),
        take(1)
      ).subscribe(() => {
        this.toggleSidebarAddRevenue('cc-Add-Revenue-sidebar');
      });
    });
  }



  get isSaving$(): Observable<boolean> {
    return this._contractFacade.updateRevenueRequestState$.pipe(
      takeUntil(this.destroy$),
      map(state => state === HttpActionState.Busy)
    );
  }

  get isLoadingConversionRates$(): Observable<boolean> {
    return this.isLoadingConversionRates.asObservable();
  }

  get isSaveDisabled$(): Observable<boolean> {
    return combineLatest([
      this.isSaving$,
      this.isLoadingConversionRates$,
      this.conversionSuccessful.asObservable()
    ]).pipe(
      map(([isSaving, isLoadingConversion, conversionSuccess]) => 
        isSaving || isLoadingConversion || !conversionSuccess
      )
    );
  }



  /**
   * Handles the conversion of revenue amounts to the system currency using the latest conversion rates.
   * 
   * This method retrieves the current contract and revenue form values, checks if a currency conversion is needed,
   * and calls the contract control service to fetch conversion rates. Upon successful conversion, it updates the
   * revenue conversion rates and displays a success toast message. If an error occurs during conversion, it displays
   * an error toast message. If no conversion is needed, it resets the conversion rates to default values.
   *
   * @remarks
   * - Uses RxJS operators to manage asynchronous data flow.
   * - Only performs conversion if the selected currency is different from the system currency and the amount is greater than zero.
   * - Updates `revenueConversationRates` with the conversion result or resets it if no conversion is performed.
   */
  conversionRates(): void {
    if (this.actionFrom === ActionEnum.CREATING_CONTRACT) {
      this.revenueForm.get('revenue_converted_amount')?.setValue(null);
      this.revenueForm.get('conversion_rate')?.setValue(null);
      this.revenueForm.get('conversion_date')?.setValue(null);
      this.conversionSuccessful.next(true);
      return;
    }

    this.contract$.pipe(
      filter(Boolean),
      take(1),
      switchMap(contract => {
        const currency = this.revenueForm.get('revenue_currency')?.value;
        const amount = this.revenueForm.get('revenue_amount')?.value as number;

        if (currency && currency !== this.systemCurrency && amount > 0) {
          this.isLoadingConversionRates.next(true);
          this.conversionSuccessful.next(false);
          return this.contractControlService.conversionRates(currency, amount, contract.id, this.revenueForm.get('id')?.value, 'revenue').pipe(
            tap(res => {
              this.revenueForm.get('revenue_converted_amount')?.setValue(res.revenue_converted_amount);
              this.revenueForm.get('conversion_rate')?.setValue(res.conversion_rate);
              this.revenueForm.get('conversion_date')?.setValue(res.conversion_date);
              this.conversionSuccessful.next(true);
              this.globalService.toastrSuccess(
                this._translocoService.translate('Update revenue currency success toast message - (Contract control section) - Converted price has been updated')
              );
            }),
            catchError(err => {
              this.globalService.toastrError(err);
              this.conversionSuccessful.next(false);
              return of(null);
            }),
            finalize(() => this.isLoadingConversionRates.next(false))
          );
        }  else {
            this.revenueForm.get('revenue_converted_amount')?.setValue(null);
            this.revenueForm.get('conversion_rate')?.setValue(null);
            this.revenueForm.get('conversion_date')?.setValue(null);
            this.conversionSuccessful.next(true);
        }
        return of(null);
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }


  /**
   * Toggle Sidebar
   *
   * @param nameRef
   */
  toggleSidebarAddRevenue(nameRef: string): void {
    this._coreSidebarService.getSidebarRegistry(nameRef)?.close();
    this.activeModal.close();
    this._ccRevenueHelperService.triggerChangeDetection(false);
  }




  /**
   * Handles the submission of the revenue form.
   * 
   * - Marks the form as submitted.
   * - Validates the form; if invalid, the method returns early.
   * - Calls the facade to update the revenue with the form values.
   * - Subscribes to the update request state observable to determine when the update is complete or has failed.
   * - On completion or failure, resets the submission state, clears the form, and closes the modal.
   */
  saveRevenue(): void {
    this.submitted = true;
    if (this.revenueForm.invalid) return;

    if(this.actionFrom === ActionEnum.CREATING_CONTRACT){
        this.activeModal.close(this.revenueForm.value);
        return;
    }
    
    this._contractFacade.updateRevenue(this.revenueForm.value);

    // Listen for the updateRevenueRequestState$ to determine when the update is done
    this._contractFacade.updateRevenueRequestState$.pipe(
      filter(x => HttpActionState.Done ===  x),
      takeUntil(this.destroy$)
    ).subscribe((x) => {
      this.resetFormProps();
      this.toggleSidebarAddRevenue('cc-Add-Revenue-sidebar');
    });
  }




  private resetFormProps(): void {
    this.revenueForm.patchValue({
        id: null,
        description: null,
        revenue_amount: null,
        billing_frequency: null,
        billing_start: null,
        revenue_currency: null,
        billing_end: null,
        has_billing_end_date: false
    });
    this.submitted = false;
  }



  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}


